/**
 * Whether a round in flight ran into anything. Named for `RB.EXE`'s own
 * `collide.c`, because it is the same test: **geometry, and only geometry.**
 *
 * The original steps every round it has fired and intersects the path it swept
 * this frame with each target's extent. There is no hit roll, no accuracy
 * statistic, and no per-shooter modifier anywhere in it — a round that passes
 * through the aeroplane hits and a round that passes beside it does not. This
 * port now does the same thing, over the round records that were already in the
 * encounter state for the renderer to draw.
 *
 * ## Why the swept segment and not the point
 *
 * At 800 m/s a round covers 13.3 m in one 60 Hz tick, and a scout is about 8 m
 * across. Testing "is the round inside the sphere *now*" would therefore miss
 * most of the hits it should score — the round is in front of the target on one
 * tick and behind it on the next. So each tick tests the *segment* the round
 * travelled, which is exact for a straight round and a stationary sphere and
 * good to a fraction of the target radius for the closing speeds of a dogfight
 * (a 50 m/s target moves 0.8 m in the same tick).
 *
 * The earliest intersection along the segment wins, so a round that would pass
 * through two aeroplanes hits the near one, and a round that hits is consumed:
 * it leaves the live list and is neither drawn nor tested again.
 */

import type { Tunables } from "../flight/tunables.ts";
import type { Vec3 } from "../flight/math3d.ts";
import { arrivalAspect, type CombatBody, type HitAspect } from "./geometry.ts";
import { roundTravelM, type RoundRecord } from "./rounds.ts";

/** Something a round can run into: a sphere with an attitude, so the hit has a face. */
export interface CollisionTarget {
  readonly id: string;
  readonly body: CombatBody;
  readonly radiusM: number;
}

export interface RoundImpact {
  readonly round: RoundRecord;
  readonly targetId: string;
  /** Metres the round had flown from its muzzle when it struck — what sizes the damage. */
  readonly rangeM: number;
  readonly point: Vec3;
  readonly aspect: HitAspect;
}

/**
 * Where along the segment `from → to` it first enters the sphere, as a fraction
 * in [0, 1], or `null` if it never does. A segment that starts *inside* the
 * sphere returns 0 — the round is already in the aeroplane.
 */
export function segmentSphereHit(from: Vec3, to: Vec3, centre: Vec3, radiusM: number): number | null {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const dz = to.z - from.z;
  const fx = from.x - centre.x;
  const fy = from.y - centre.y;
  const fz = from.z - centre.z;
  const a = dx * dx + dy * dy + dz * dz;
  if (a < 1e-12) return null;
  const b = 2 * (fx * dx + fy * dy + fz * dz);
  const c = fx * fx + fy * fy + fz * fz - radiusM * radiusM;
  if (c <= 0) return 0;
  const discriminant = b * b - 4 * a * c;
  if (discriminant < 0) return null;
  const entry = (-b - Math.sqrt(discriminant)) / (2 * a);
  if (entry < 0 || entry > 1) return null;
  return entry;
}

export interface SweepInput {
  readonly rounds: readonly RoundRecord[];
  /** What this round's shooter may hit. Keyed by shooter so nobody shoots themselves. */
  readonly targetsFor: (shooterId: string) => readonly CollisionTarget[];
  readonly tick: number;
  readonly dt: number;
  readonly tunables: Tunables;
}

export interface SweepResult {
  readonly impacts: readonly RoundImpact[];
  /** The rounds still in the air — every one that did not strike something. */
  readonly survivors: readonly RoundRecord[];
}

function pointAlong(round: RoundRecord, travelM: number): Vec3 {
  return {
    x: round.from.x + round.direction.x * travelM,
    y: round.from.y + round.direction.y * travelM,
    z: round.from.z + round.direction.z * travelM,
  };
}

/** One tick of collision for every round in the air. */
export function sweepRounds(input: SweepInput): SweepResult {
  const impacts: RoundImpact[] = [];
  const survivors: RoundRecord[] = [];
  for (const round of input.rounds) {
    const startM = roundTravelM(round, input.tick - 1, input.dt, input.tunables);
    const endM = roundTravelM(round, input.tick, input.dt, input.tunables);
    if (endM <= startM) {
      survivors.push(round);
      continue;
    }
    const from = pointAlong(round, startM);
    const to = pointAlong(round, endM);

    let nearest: CollisionTarget | null = null;
    let nearestFraction = Number.POSITIVE_INFINITY;
    for (const target of input.targetsFor(round.shooterId)) {
      const fraction = segmentSphereHit(from, to, target.body.position, target.radiusM);
      if (fraction !== null && fraction < nearestFraction) {
        nearest = target;
        nearestFraction = fraction;
      }
    }
    if (!nearest) {
      survivors.push(round);
      continue;
    }
    impacts.push({
      round,
      targetId: nearest.id,
      rangeM: startM + (endM - startM) * nearestFraction,
      point: pointAlong(round, startM + (endM - startM) * nearestFraction),
      aspect: arrivalAspect(nearest.body, round.direction),
    });
  }
  return { impacts, survivors };
}
